import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getTemplateDetail, getTemplatesRulesGroup } from 'services/templateService'
import { createOrder, getOrdersCode, getConfigurationChutesForTemplate } from 'services/ordersService'
import HeaderPageSection from 'core/presentation/components/molecules/HeaderPageSection/HeaderPageSection'
import { AppSelectSearchable } from 'components/atoms/AppSelectSearchable/AppSelectSearchable'
import { TextArea } from 'components/atoms/TextArea/TextArea'
import { AccordionRules } from 'components/molecules/AccordionRule/AccordionRule'
import AppButton from 'components/atoms/AppButton/AppButton'
import Message from 'components/atoms/Message/Message'
import PreviewEmpty from '../../../orders/presentation/components/molecules/PreviewEmpty'
import { useOperationModes } from 'core/hooks/useOperationModes'
import { useOrderContainerization } from 'orders/presentation/hooks/useOrderContainerization'
import WithoutContainerizationSwitch from 'orders/presentation/components/atoms/WithoutContainerizationSwitch/WithoutContainerizationSwitch'
import { OperationMode } from 'core/domain/operationMode'
import { PreviewChutes } from 'components/molecules/PreviewChutes/PreviewChutes'
import { StateChute } from 'interfaces/chutes'
import { AppInputDate } from 'components/atoms/AppInputDate/AppInputDate'
import { Badge } from 'components/atoms/Badge/Badge'
import { getPackagesAvailable } from 'services/packageService'
import { DateISO } from 'interfaces/date'
import PackageIcon from 'assets/packageIcon.png'

type PreviewChute = { number: number; state: StateChute }
type TemplateOption = { value: string; label: string, groups_count: string }

// El backend genera los contenedores de forma asincrónica tras crear la orden.
// Esperamos este lapso antes de redirigir al listado para que la orden ya se
// muestre "En proceso" y no "Pendiente".
const ORDER_CREATION_DELAY_MS = 5000

const DEFAULT_PREVIEW_CHUTES: PreviewChute[] = Array.from({ length: 26 }, (_, i) => ({
    number: i + 1,
    state: 'available' as StateChute,
}))


const buildPreviewChutes = (apiChutes: { chute: number; type: string }[]): PreviewChute[] => {
    const chuteMap = new Map(apiChutes.map((c) => [c.chute, c.type]))
    return DEFAULT_PREVIEW_CHUTES.map((c) => {
        const type = chuteMap.get(c.number)
        if (type === 'not_available') return { number: c.number, state: 'not_available' as StateChute }
        if (type)                     return { number: c.number, state: 'assigned' as StateChute }
        return { number: c.number, state: 'available' as StateChute }
    })
}

export const CreateOrders = () => {
    const navigate = useNavigate()

    const [code, setCode] = useState<null | string>(null)
    const [templates, setTemplates] = useState<TemplateOption[]>([])
    const [templateDate, setTemplateDate] = useState<DateISO | string>('')
    const [textDescription, setTextDescription] = useState('')
    const [quantity, setQuantity] = useState(0)
    const [error, setError] = useState<null | string>(null)
    const [templateSelected, setTemplateSelected] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [rules, setRules] = useState([])
    const [previewChutes, setPreviewChutes] = useState<PreviewChute[]>(DEFAULT_PREVIEW_CHUTES)

    // Modo de operación
    const { operationModes, loading: loadingOperationModes, isSorterMode, isClassificationMode, findById } = useOperationModes()
    const [selectedOperationMode, setSelectedOperationMode] = useState<OperationMode | null>(null)
    const { withoutContainerization, setWithoutContainerization } = useOrderContainerization()

    const breadcrumbNavigate = [
        { label: 'Ordenes', path: '/ordenes' },
        { label: 'Cargar orden', path: '/ordenes' },
    ]

    const handleSelectTemplate = (value: string) => {
        setTemplateSelected(value)
    }

    const handleOperationModeChange = (code: string, id: number) => {
        const mode = findById(id)
        setSelectedOperationMode(mode)
    }

    const sendData = async () => {
        // Guard against double submit: ignore clicks while a request is in flight.
        if (submitting) return

        if (!selectedOperationMode) {
            setError('Debe seleccionar un modo de operación')
            setTimeout(() => setError(null), 5000)
            return
        }

        setSubmitting(true)
        try {
            await createOrder({
                description: textDescription,
                template_id: Number(templateSelected),
                classification_type_id: selectedOperationMode.id,
                created_after_than: templateDate,
                created_before_than: '',
                // El switch indica si la orden TIENE contenerización; el campo de
                // API es la negación: activado (con) -> false; desactivado (sin) -> true.
                without_containerization: !withoutContainerization,
            })
            // Backend genera los contenedores de forma asincrónica tras crear la
            // orden. Esperamos antes de navegar para que en el listado la orden
            // ya aparezca "En proceso" y no "Pendiente". El botón permanece en
            // estado de carga durante toda la espera (submitting sigue en true).
            await new Promise((resolve) => setTimeout(resolve, ORDER_CREATION_DELAY_MS))
            navigate('/orders', { state: { successMessage: 'Orden creada con éxito.' } })
        } catch (err) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setError((err as any).message)
            setTimeout(() => setError(null), 5000)
            // Solo liberamos el botón en error: en éxito navegamos y la vista
            // se desmonta, así que no reactivamos el submit.
            setSubmitting(false)
        }
    }

    useEffect(() => {
        const fetchInitial = async () => {
            const response = await getOrdersCode()
            setCode(response.data.code)
        }
        fetchInitial()
    }, [])

    useEffect(() => {
        if (operationModes.length > 0 && !selectedOperationMode) {
            const sorterMode = operationModes.find((mode) => mode.code === 'sorter')
            if (sorterMode) {
                setSelectedOperationMode(sorterMode)
            }
        }
    }, [operationModes, selectedOperationMode])

    useEffect(() => {
        if (loadingOperationModes) return
        if (!selectedOperationMode) return

        const fetchTemplates = async () => {
            const { data } = await getTemplatesRulesGroup(selectedOperationMode.id)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const mapped: TemplateOption[] = data.map((item: any) => ({
                value: String(item.id),
                label: item.name,
                groups_count: item.groups_count
            }))
            setTemplates(mapped)
            // No se autoselecciona ninguna plantilla: el usuario debe elegirla.
            setTemplateSelected('')
        }
        fetchTemplates()
    }, [selectedOperationMode, loadingOperationModes])

    // Cuando cambia la plantilla: carga reglas y chutes asignados
    useEffect(() => {
        if (templateSelected === '') return

        const fetchDetail = async () => {
            const response = await getTemplateDetail(Number(templateSelected))
            setRules(response.groups)
        }

        const fetchChutes = async () => {
            try {
                const response = await getConfigurationChutesForTemplate(Number(templateSelected))
                const apiChutes = response.data.default_chutes ?? []
                setPreviewChutes(buildPreviewChutes(apiChutes))
            } catch {
                setPreviewChutes(DEFAULT_PREVIEW_CHUTES)
            }
        }

        fetchDetail()
        fetchChutes()
    }, [templateSelected])

    // Cargar cantidad de paquetes disponibles cuando cambia la fecha
    useEffect(() => {
        const fetchPackagesAvailable = async () => {
            const count = await getPackagesAvailable(templateDate as DateISO)
            setQuantity(count.data)
        }
        fetchPackagesAvailable()
    }, [templateDate])


    const canSubmit = templateSelected !== '' && selectedOperationMode !== null

    return (
        <>
            <HeaderPageSection
                title="Cargar orden con criterios"
                showGoBack={false}
                breadcrumbItems={breadcrumbNavigate}
            />
            {error !== null && <Message message={error} type={'error'} />}
            <div className="bg-white rounded-lg">
                <div className="flex flex-wrap p-[32px] gap-4">
                    <div className="flex gap-4 w-full">
                        {/* Columna izquierda — formulario */}
                        <div className="flex-1">
                            <h2 className="text-[20px] font-semibold mb-[32px]">
                                Orden #{code}
                            </h2>

                            {/* Sin contenerización */}
                            <div className="gap-2 flex justify-between mb-[32px]">
                                <h2>Contenerización</h2>
                                <div className="w-[65%]">
                                    <WithoutContainerizationSwitch
                                        checked={withoutContainerization}
                                        onChange={setWithoutContainerization}
                                    />
                                </div>
                            </div>

                            {/* Modo de Operación */}
                            <div className="gap-2 flex justify-between mb-[32px]">
                                <h2>Modo de Operación*</h2>
                                <div className="w-[65%]">
                                    <div className="flex items-center gap-6">
                                        {operationModes.map((mode) => {
                                            const isChecked = selectedOperationMode?.code === mode.code
                                            return (
                                                <label
                                                    key={mode.id}
                                                    className="flex items-center gap-2 cursor-pointer"
                                                    onClick={() => handleOperationModeChange(mode.code, mode.id)}
                                                >
                                                    {/* Radio custom */}
                                                    <div
                                                        className="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                                                        style={{
                                                            borderColor: isChecked ? '#568C13' : '#CBD5E1',
                                                            backgroundColor: isChecked ? '#568C13' : 'white',
                                                        }}
                                                    >
                                                        {isChecked && (
                                                            <div className="w-[6px] h-[6px] rounded-full bg-white" />
                                                        )}
                                                    </div>
                                                    <span className="text-sm text-[#070A13]">
                                                        {mode.name}
                                                    </span>
                                                </label>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Plantilla */}
                            <div className="gap-2 flex justify-between mb-[32px]">
                                <h2>Plantilla*</h2>
                                <div className="w-[65%]">
                                    <AppSelectSearchable
                                        options={templates}
                                        onChange={handleSelectTemplate}
                                        placeholder="Seleccionar uno"
                                        isSearchable
                                        value={templateSelected}
                                    />
                                </div>
                            </div>

                            {/* Sección "Desde" - Solo visible para modo Sorter */}
                            {isSorterMode(selectedOperationMode) && (
                                <div className="gap-2 flex justify-between  mb-[32px]">
                                    <p>Desde*</p>
                                    <div className="w-[65%]">
                                        <div className="mb-[12px]">
                                            <AppInputDate
                                                onChange={(e: string) => {
                                                    setTemplateDate(e)
                                                }}
                                                placeholder="Todos los tiempos"
                                                allDays={false}
                                            />
                                        </div>
                                        <Badge
                                            icon={<img src={PackageIcon} />}
                                            text={`${quantity} paquetes disponibles para procesar en la orden`}
                                        />
                                    </div>
                                </div>
                            )}


                            {/* Descripción */}
                            <div className="gap-2 justify-between flex">
                                <p>Descripción (opcional)</p>
                                <div className="w-[65%]">
                                    <TextArea
                                        placeholder="Escribe algunas líneas para describir a la orden"
                                        onChange={(value: string) =>
                                            setTextDescription(value)
                                        }
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="border"></div>

                        {/* Columna derecha — previsualización */}
                        <div className="flex-1 w-full max-w-[36%]">
                            <h2 className="text-[16px] font-semibold mb-4">
                                Previsualización
                            </h2>

                            {!selectedOperationMode && <PreviewEmpty />}

                            {isSorterMode(selectedOperationMode) && (
                                <PreviewChutes chutes={previewChutes} />
                            )}

                            {isClassificationMode(selectedOperationMode) && (
                                <>
                                    {`Reglas: ${templates.find(t => t.value === templateSelected)?.groups_count ?? 0}`}
                                </>
                            )}

                            {/* Reglas de la plantilla */}
                            {templateSelected !== '' && (
                                <div className="mt-4">
                                    <AccordionRules rules={rules} isClassification={isClassificationMode(selectedOperationMode)} />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end border-t-2 pt-[32px] gap-[12px] w-full">
                        <AppButton
                            title="Descartar"
                            state="ghost"
                            onClick={() => navigate(-1)}
                        />
                        <AppButton
                            title="Cargar orden"
                            onClick={sendData}
                            disabled={!canSubmit}
                            loading={submitting}
                        />
                    </div>
                </div>
            </div>
        </>
    )
}
